import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Html5Qrcode } from 'html5-qrcode';
import { CheckCircle2, XCircle, QrCode } from 'lucide-react';

import Button from '../../components/ui/Button';
import { getGuestVisit, guestScan } from '../../services/guestService';

const Page = styled.div`
  min-height: 100vh;
  background: ${(p) => p.theme.colors.bgColor};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px;
  font-family: ${(p) => p.theme.metrics.font.family};
`;

const Card = styled.div`
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
`;

const TenantName = styled.h1`
  margin: 0 0 4px;
  font-size: ${(p) => p.theme.metrics.font.size.xl};
  color: ${(p) => p.theme.colors.text.primary};
  text-align: center;
`;

const Title = styled.div`
  text-align: center;
  color: ${(p) => p.theme.colors.primary.main};
  font-weight: ${(p) => p.theme.metrics.font.weight.bold};
  margin-bottom: 16px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f2f5;
  font-size: ${(p) => p.theme.metrics.font.size.sm};
  color: ${(p) => p.theme.colors.text.primary};

  span:first-child { opacity: 0.6; }
  span:last-child { text-align: right; font-weight: ${(p) => p.theme.metrics.font.weight.semiBold}; }
`;

// #72 — yerel ScanButton (16px dolgu, md/bold font — kendisi de "devasa"
// örneklerden biriydi) kaldırıldı, paylaşılan `Button fullWidth` (bkz.
// t6-report.md).

const Info = styled.div`
  margin-top: 16px;
  padding: 12px;
  border-radius: 8px;
  background: #fff7e6;
  color: #8a6d3b;
  font-size: ${(p) => p.theme.metrics.font.size.sm};
  text-align: center;
`;

const ReaderBox = styled.div`
  width: 100%;
  border-radius: 12px;
  overflow: hidden;

  #guest-qr-reader { width: 100%; }
`;

const Result = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
  text-align: center;

  h2 { margin: 0; color: ${(p) => (p.$granted ? '#1d9e74' : '#d64545')}; }
  p { margin: 0; color: ${(p) => p.theme.colors.text.primary}; opacity: 0.75; }
`;

const PHASES = { LOADING: 'loading', INVALID: 'invalid', SUMMARY: 'summary', SCANNING: 'scanning', RESULT: 'result' };
const SUPPORTED_LANGS = ['tr', 'en', 'ru', 'kk', 'az'];

export default function GuestVisitPage() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();

  const [phase, setPhase] = useState(PHASES.LOADING);
  const [visit, setVisit] = useState(null);
  const [result, setResult] = useState(null);
  const [uiError, setUiError] = useState(null);

  // Ziyaretçi panel kullanıcısı değil — kayıtlı dil tercihi yoksa tarayıcı diline geç
  useEffect(() => {
    if (!localStorage.getItem('flexbee_lang')) {
      const browserLang = (navigator.language || 'tr').slice(0, 2).toLowerCase();
      if (SUPPORTED_LANGS.includes(browserLang) && browserLang !== i18n.language) {
        i18n.changeLanguage(browserLang);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getGuestVisit(token)
      .then((res) => {
        setVisit(res.data?.data || null);
        setPhase(PHASES.SUMMARY);
      })
      .catch(() => setPhase(PHASES.INVALID));
  }, [token]);

  const handleDecoded = useCallback(async (qrToken) => {
    let pos;
    try {
      pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });
      });
    } catch {
      setUiError(t('guest.locationDenied'));
      setPhase(PHASES.SUMMARY);
      return;
    }
    try {
      const res = await guestScan({
        token,
        qrToken,
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracyMeters: pos.coords.accuracy,
      });
      setResult(res.data?.data || { granted: false, denyReason: 'visit.invalidLink' });
    } catch (err) {
      setResult({ granted: false, denyReason: err?.response?.data?.message || 'visit.invalidLink' });
    }
    setPhase(PHASES.RESULT);
  }, [token, t]);

  // Kamera yaşam döngüsü — yalnız SCANNING fazında aktif
  useEffect(() => {
    if (phase !== PHASES.SCANNING) return undefined;
    const scanner = new Html5Qrcode('guest-qr-reader');
    let finished = false;
    scanner
      .start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          if (finished) return;
          finished = true;
          scanner.stop().catch(() => {}).finally(() => handleDecoded(decodedText));
        },
        () => {}, // kare bazlı decode hataları normaldir — yoksay
      )
      .catch(() => {
        setUiError(t('guest.cameraDenied'));
        setPhase(PHASES.SUMMARY);
      });
    return () => {
      if (!finished) {
        // html5-qrcode: scanner hiç start olmadıysa stop() senkron fırlatır — yut
        try { scanner.stop().catch(() => {}); } catch { /* başlatılamadı */ }
      }
    };
  }, [phase, handleDecoded, t]);

  const fmt = (d) => (d ? new Date(d).toLocaleString(i18n.language) : '-');

  if (phase === PHASES.LOADING) {
    return <Page><Card>{t('guest.loading')}</Card></Page>;
  }
  if (phase === PHASES.INVALID) {
    return <Page><Card>{t('guest.invalidLink')}</Card></Page>;
  }

  const statusInfoKey = {
    expired: 'guest.statusInfoExpired',
    canceled: 'guest.statusInfoCanceled',
    rejected: 'guest.statusInfoRejected',
    waiting: 'guest.statusInfoWaiting',
  }[visit?.status];

  return (
    <Page>
      <Card>
        <TenantName>{visit?.tenantName}</TenantName>
        <Title>{t('guest.title')}</Title>

        {phase === PHASES.RESULT ? (
          <Result $granted={result?.granted}>
            {result?.granted
              ? <CheckCircle2 size={72} color="#1d9e74" />
              : <XCircle size={72} color="#d64545" />}
            <h2>{result?.granted ? t('guest.accessGranted') : t('guest.accessDenied')}</h2>
            {result?.gateName && <p>{result.gateName}</p>}
            {!result?.granted && result?.denyReason && (
              <p>{t(`responseErrors.${result.denyReason}`, { defaultValue: result.denyReason })}</p>
            )}
            <Button fullWidth onClick={() => { setResult(null); setPhase(PHASES.SCANNING); }}>
              <QrCode size={20} /> {t('guest.scanAgain')}
            </Button>
          </Result>
        ) : phase === PHASES.SCANNING ? (
          <>
            <ReaderBox><div id="guest-qr-reader" /></ReaderBox>
            <Info>{t('guest.scanHint')}</Info>
          </>
        ) : (
          <>
            <Row><span>{t('visit.visitorFullName')}</span><span>{visit?.visitorFullName}</span></Row>
            {visit?.hostEmployeeName && (
              <Row><span>{t('guest.host')}</span><span>{visit.hostEmployeeName}</span></Row>
            )}
            <Row>
              <span>{t('guest.validBetween')}</span>
              <span>{fmt(visit?.validFrom)} — {fmt(visit?.validTo)}</span>
            </Row>
            {visit?.dailyTimeStart && visit?.dailyTimeEnd && (
              <Row>
                <span>{t('guest.dailyHours')}</span>
                <span>{visit.dailyTimeStart} — {visit.dailyTimeEnd}</span>
              </Row>
            )}
            {visit?.gateNames?.length > 0 && (
              <Row>
                <span>{t('guest.authorizedGates')}</span>
                <span>{visit.gateNames.join(', ')}</span>
              </Row>
            )}

            {uiError && <Info>{uiError}</Info>}
            {statusInfoKey ? (
              <Info>{t(statusInfoKey)}</Info>
            ) : visit?.status === 'approved' && (
              <Button fullWidth onClick={() => { setUiError(null); setPhase(PHASES.SCANNING); }}>
                <QrCode size={20} /> {t('guest.scanButton')}
              </Button>
            )}
          </>
        )}
      </Card>
    </Page>
  );
}
